import {useCallback, useEffect, useRef, useState} from 'react';

import {useBannerDismissal} from '../../agentChat/useBannerDismissal';
import {hasDismissedLocally} from '../../agentChat/useDismissibleNotice';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

const INTRO_NOTICE_ID = 'wbagent-intro';

type IntroTooltip = {
  show: boolean;
  hide: () => void;
};

/**
 * useWBAgentIntroTooltip reports whether to show the nav button's intro
 * tooltip, and records the impression so it appears once per account.
 *
 * Pass `enabled: false` while the agent is unavailable, so the tooltip neither
 * shows nor records.
 */
export function useWBAgentIntroTooltip(enabled: boolean): IntroTooltip {
  const {dismissed, loading, error, dismiss} = useBannerDismissal(
    enabled ? INTRO_NOTICE_ID : null
  );

  // TODO(WB-38447): Drop this and `hasDismissedLocally` once the backfill has
  // shipped for a release.
  const dismissedLocallyRef = useRef(hasDismissedLocally(INTRO_NOTICE_ID));

  const [show, setShow] = useState(false);
  // Deciding once keeps a reverted optimistic write from re-showing a tooltip
  // the user already opened past.
  const hasDecidedTooltipRef = useRef(false);
  // NOTE: the button is pressable while the dismissal is still in flight, so a
  // press has to outlive the decision below.
  const hasInteractedRef = useRef(false);

  useEffect(() => {
    if (hasDecidedTooltipRef.current || !enabled || loading || error != null) {
      return;
    }
    hasDecidedTooltipRef.current = true;

    if (dismissed) {
      return;
    }

    // Fires even when the checks below suppress it. That record is the
    // backfill for a local dismissal, and a user with the panel already open
    // has found ARIA.
    dismiss?.();

    if (
      dismissedLocallyRef.current ||
      hasInteractedRef.current ||
      useWBAgentWindowStore.getState().open
    ) {
      return;
    }
    setShow(true);
  }, [enabled, loading, error, dismissed, dismiss]);

  const hide = useCallback(() => {
    hasInteractedRef.current = true;
    setShow(false);
  }, []);

  return {show, hide};
}
